/* eslint-disable no-labels */
/* eslint-disable no-console */
import { AnimatePresence, motion } from 'framer-motion';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Team5Layout } from './components/Team5Layout';

import { Button, Card, Loader, Modal, NavBar } from '@/components';
import { FieldLayout } from '@/components/Layouts/FieldLayoutF5';
import { DetailsPlayer } from '@/components/Team/';
import { DetailsPlayerSubmit } from '@/components/Team/DetailsPlayer';
import { FormFilterPlayers } from '@/components/Team/FormFilterPlayers';
import { DesktopSidebar, MobileSidebar } from '@/components/Team/Sidebar';
import useAuth from '@/hooks/useAuth';
import useHostbackButton from '@/hooks/useHostbackButton';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { useTeam } from '@/hooks/useTeam5';
import { useTeamStore } from '@/hooks/useTeamStore';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  getInitBuildTeam5,
  addPlayer,
  removePlayer,
  setCaptain,
  setInsured,
  saveTeam,
} from '@/services/buildTeam5';
import { TeamActionTypes, TeamStateReducer } from '@/store';
import { GDTApiErrors, PlayerPositionAbbrF5 } from '@/types';
import {
  EnumTeamContext,
  PlayerF5,
  PlayersOnFieldF5,
  RequestAgregarJugadorF5,
  RequestDesignarAseguradoJugadorF5,
  RequestDesignarCapitanJugadorF5,
  RequestEliminarJugadorF5,
  ResponseGetInitBuildF5,
  TemplatePlayer,
} from '@/types/team';
import { PlayerMyTeamF5 } from '@/types/teamf5';
import {
  MAX_INPUT_RANGE,
  MIN_INPUT_RANGE,
  isSelectedPlayerFromList,
  SLIDE_IN_OUT_LEFT,
  TEXTS,
  POSITIONS_LABELS,
  getAvailablePositionOnFieldF5,
  parseJSXElementToPlayerClub,
  checkTeamHasCaptain,
  resolvePlayerPrice,
  uppercaseFirstLetter,
} from '@/utils';

export const Team5 = () => {
  const templatePlayer = usePlayerTemplateMap();
  const { userInitialData } = useAuth();
  const { isDesktop, isMobile } = useIsDesktop();
  const toast = useToast();
  const { state, dispatch } = useTeamStore();
  const { getPlayersOnField } = useTeam();
  const navigate = useNavigate();

  // Get init data team5
  const { isLoading: isLoadingGetTeam, isFetching: isFechingGetTeam } =
    useQuery('getBuildTeamF5', async () => await getInitBuildTeam5(), {
      onSuccess: (data) => {
        if (data?.estado) {
          const teamF5 = data.equipo_f5.jugadores.map((player, index) => {
            const playerFromTemplate = templatePlayer.get(player.id);
            return {
              ...player,
              cotizacion: resolvePlayerPrice(playerFromTemplate?.cot),
              orden: index,
            };
          });
          checkTeamIsComplete(data.equipo_f5.jugadores);
          handleSetTeamOnField(teamF5);
        }
      },
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    });

  // sumo nuevo jugador
  const { mutateAsync: addNewPlayer, isLoading: isLoadingAddPlayerToTeam } =
    useMutation(
      'setNewPlayer',
      async (selectedPlayer: RequestAgregarJugadorF5) =>
        await addPlayer(selectedPlayer),
      {
        onSuccess(data) {
          if (data.estado) {
            checkTeamIsComplete(data.equipo_f5.jugadores);
          }
        },
      }
    );

  // sumo nuevo capitan
  const {
    mutateAsync: designatePlayerAsCaptain,
    isLoading: isLoadingDesignateCaptainToPlayer,
  } = useMutation(
    async (selectedPlayer: RequestDesignarCapitanJugadorF5) =>
      await setCaptain(selectedPlayer)
  );

  // sumo nuevo asegurado
  const {
    mutateAsync: designatePlayerAsInsuranced,
    isLoading: isLoadingDesignateInsurancedToPlayer,
  } = useMutation(
    async (selectedPlayer: RequestDesignarAseguradoJugadorF5) =>
      await setInsured(selectedPlayer)
  );

  // borro jugador de equipo
  const {
    mutateAsync: removePlayerFromTeam,
    isLoading: isLoadingRemovePlayerFromTeam,
  } = useMutation(
    async (selectedPlayer: RequestEliminarJugadorF5) =>
      await removePlayer(selectedPlayer)
  );

  // guardo equipo final
  const { mutateAsync: submitNewTeam, isLoading: isLoadingSaveTeam } =
    useMutation(async () => await saveTeam());

  useEffect(() => {
    navigate('', { replace: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackButton = useCallback(() => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        ...(!state.showFiltersPlayersList
          ? {
              selectedPlayerOnBuildTeam: null,
            }
          : { showFiltersPlayersList: false }),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showFiltersPlayersList]);
  useHostbackButton(handleBackButton);

  const [componentIsLoading, setComponentIsLoading] = useState(true);

  const selectedPositionLabel = useMemo(() => {
    if (state.selectedPosition !== POSITIONS_LABELS.abbreviation.ARQ) {
      return 'jugador';
    }
    return POSITIONS_LABELS.spanish_singular.arquero;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedPosition, state.playersOnFieldF5]);

  const checkCaptainAndInsurancedAndShowMessage = useCallback(
    (players: PlayerF5[]) => {
      const hasCaptainAndInsuranced = checkCaptainAndInsurancedProps(players, [
        'seguro',
        'capitan',
      ]);
      const hasOnlyCaptain = checkCaptainAndInsurancedProps(players, [
        'capitan',
      ]);
      const hasOnlyInsuranced = checkCaptainAndInsurancedProps(players, [
        'seguro',
      ]);

      // Si tiene ambos, corto ejecución.
      if (hasCaptainAndInsuranced) return;

      let messageToShow = `${TEXTS.team.pickCaptainAndInsuranced[1]} y ${TEXTS.team.pickCaptainAndInsuranced[2]}`;
      if (hasOnlyCaptain) {
        messageToShow = TEXTS.team.pickCaptainAndInsuranced[2];
      } else if (hasOnlyInsuranced) {
        messageToShow = TEXTS.team.pickCaptainAndInsuranced[1];
      }

      toast.show(`${TEXTS.team.pickCaptainAndInsuranced[0]} ${messageToShow}`);
    },
    [toast]
  );

  const checkTeamIsComplete = useCallback(
    (teamF5: PlayerF5[]) => {
      const teamIsCompleted =
        teamF5.length > 0 && teamF5.every((player) => player.id !== undefined);

      if (teamIsCompleted) {
        checkCaptainAndInsurancedAndShowMessage(teamF5);
      }
    },
    [checkCaptainAndInsurancedAndShowMessage]
  );

  useEffect(() => {
    if (componentIsLoading) {
      setComponentIsLoading(false);
    }
  }, [componentIsLoading]);

  // Effect para validar si el equipo esta completo
  // Tomando en cuenta el capitan + las posiciones completas
  useEffect(() => {
    const allPlayers = state.playersOnFieldF5?.fieldPlayers
      ? [...state.playersOnFieldF5?.fieldPlayers]
      : [];
    if (state.playersOnFieldF5?.goalkeeper) {
      allPlayers.push(state.playersOnFieldF5?.goalkeeper);
    }
    const teamIsCompleted =
      allPlayers.length > 0 &&
      allPlayers.every((player) => player.props.data.id !== undefined);

    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        teamIsComplete: teamIsCompleted,
      },
    });
  }, [dispatch, state.playersOnFieldF5]);

  const checkCaptainAndInsurancedProps = (
    allPlayers: PlayerF5[],
    requiredProps: (keyof PlayerF5)[]
  ) => {
    const isValid = requiredProps.every((prop) =>
      allPlayers.some((player) => player[prop] === true)
    );

    return isValid;
  };

  const validateIfCaptainAndInsurancedHasSelected = (
    playersOnFieldF5: PlayersOnFieldF5
  ) => {
    const allPlayersJsx = playersOnFieldF5?.fieldPlayers
      ? [...playersOnFieldF5?.fieldPlayers]
      : [];
    if (playersOnFieldF5?.goalkeeper) {
      allPlayersJsx.push(playersOnFieldF5?.goalkeeper);
    }

    const allPlayers = allPlayersJsx.map(
      (player: JSX.Element) => player.props.data
    );

    const teamIsCompleted =
      allPlayersJsx.length > 0 &&
      allPlayersJsx.every((player) => player.props.data.id !== undefined);
    const teamHasCaptainAndInsuranced = checkCaptainAndInsurancedProps(
      allPlayers,
      ['capitan', 'seguro']
    );
    const isValid = teamIsCompleted && teamHasCaptainAndInsuranced;

    return isValid;
  };

  const confirmTeamToService = async () => {
    const requestOK = await submitNewTeam();
    if (requestOK.estado === true) {
      navigate(ROUTES.teamConfirmF5, { replace: true });
    } else {
      toast.show(requestOK.mensaje);
    }
  };

  const handleResetActions = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        selectedPosition: null,
        selectedPlayerOnList: null,
        selectedPlayerOnTeam: null,
        selectedPlayerOnBuildTeam: null,
        showPlayersList: false,
        showFiltersPlayersList: false,
        showModalChangePlayer: false,
        filtersApplied: {
          intoBudget: false,
          orderBy: null,
          orderClub: null,
          valuesRange: [MIN_INPUT_RANGE, MAX_INPUT_RANGE],
          position: null,
          positionF5: null,
        },
      },
    });
  };

  useEffect(() => {
    handleResetActions();

    return () => {
      handleResetActions();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitNewTeam = async () => {
    const canConfirmTeam =
      state.playersOnFieldF5 &&
      validateIfCaptainAndInsurancedHasSelected(state.playersOnFieldF5);

    // Solo puedo confirmar el equipo si está completo y tiene capitán y asegurado seleccionado.
    if (!canConfirmTeam) {
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          insurancedAndCaptainPlayerModalOpen: true,
        },
      });
    } else {
      confirmTeamToService();
    }
  };

  const setTeam = (newTeamDraft: ResponseGetInitBuildF5) => {
    if (newTeamDraft.equipo_f5?.jugadores) {
      const players = newTeamDraft.equipo_f5?.jugadores.map(
        (player: PlayerF5, index: number) => {
          if (player.id) {
            const infoPlayer = templatePlayer.get(player.id);
            return {
              ...player,
              ...(infoPlayer && {
                cotizacion: resolvePlayerPrice(infoPlayer.cot),
              }),
            };
          }
          player.orden = index;
          return player;
        }
      );
      return handleSetTeamOnField(players);
    }

    toast.show(TEXTS.defaultError);
  };

  const handleGetNextPosition = (
    response: ResponseGetInitBuildF5 | GDTApiErrors,
    playerName: string,
    playerLastName: string
  ) => {
    let updatedTeamOnField: PlayersOnFieldF5 | undefined;
    let teamHasCaptain = false;
    let availablePosition:
      | {
          position: PlayerPositionAbbrF5;
          orden: number | undefined;
        }
      | undefined;

    if (response.estado) {
      const isCodeErrorInResponse =
        'codError' in response && (response.codError as boolean);
      updatedTeamOnField = setTeam(response);

      if (updatedTeamOnField) {
        const goalkeeper = updatedTeamOnField.goalkeeper as JSX.Element;
        const fieldPlayers = [
          ...updatedTeamOnField.fieldPlayers,
        ] as JSX.Element[];

        availablePosition = getAvailablePositionOnFieldF5({
          fieldPlayers,
          goalkeeper,
        });

        const teamData = parseJSXElementToPlayerClub([
          ...[goalkeeper],
          ...fieldPlayers,
        ]);

        teamHasCaptain = checkTeamHasCaptain(teamData);
      }

      return {
        ...(!availablePosition && {
          teamHasCaptain,
        }),
        ...(availablePosition && { availablePosition }),
        mensaje: `${playerLastName} ${playerName} ${TEXTS.team.playerAddedOnTeamDraft}`,
        ...(isCodeErrorInResponse && { codError: response.codError }),
      };
    }

    return {
      mensaje: response.mensaje,
      ...(response.codError && { codError: response.codError }),
    };
  };

  const deletePlayerFromTeam = async (player: TemplatePlayer) => {
    const playerToDelete = {
      idJugadorTorneoSaliente: player.id,
    };

    const response = await removePlayerFromTeam(playerToDelete);
    if (response.estado) {
      setTeam(response);
    } else {
      toast.show(response.mensaje);
    }
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        selectedPlayerOnList: null,
        selectedPlayerOnBuildTeam: null,
        selectedPosition: null,
        playerDetailsModalOpen: false,
      },
    });
  };

  const designateAsCaptain = async (player: TemplatePlayer) => {
    if (state.selectedPlayerOnBuildTeam) {
      const { id: selectedPlayerId } = player;

      const response = await designatePlayerAsCaptain({
        idJugadorTorneo: selectedPlayerId,
      });
      const playerFromTemplate = templatePlayer.get(player.id);
      if (response.estado && playerFromTemplate) {
        setTeam(response);
        toast.show(TEXTS.team.captainPicked);
      } else {
        toast.show(response?.mensaje);
      }
      // Limpio seleccion de jugador en cancha
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          selectedPlayerOnList: null,
          selectedPlayerOnBuildTeam: null,
          selectedPosition: null,
          playerDetailsModalOpen: false,
        },
      });
    }
  };

  const designateAsInsuranced = async (player: TemplatePlayer) => {
    if (state.selectedPlayerOnBuildTeam) {
      const { id: selectedPlayerId } = player;

      const response = await designatePlayerAsInsuranced({
        idJugadorTorneo: selectedPlayerId,
      });
      const playerFromTemplate = templatePlayer.get(player.id);
      if (response.estado && playerFromTemplate) {
        setTeam(response);
        toast.show(TEXTS.team.insurancedPickedF5);
      } else {
        toast.show(response?.mensaje);
      }
      // Limpio seleccion de jugador en cancha
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          selectedPlayerOnList: null,
          selectedPlayerOnBuildTeam: null,
          selectedPosition: null,
          playerDetailsModalOpen: false,
        },
      });
    }
  };

  const validateIfPlayerAlreadySelected = (player: TemplatePlayer) => {
    const existOnTeam = state.teamF5?.some(
      (playerF5) => playerF5.id === player.id
    );

    if (existOnTeam) {
      toast.show(TEXTS.team.playerAlreadyExists);
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          playerDetailsModalOpen: false,
        },
      });
    }

    return existOnTeam;
  };

  // Función para agregar un jugador a la cancha.
  const addPlayerToTeam = async (
    player: TemplatePlayer,
    teamDraft: PlayerF5[]
  ) => {
    const { id: selectedPlayerId, n: nombre, a: apellido } = player;

    // Valido si el usuario ya pertenece al equipo. En caso afirmativo, corto ejecución.
    if (validateIfPlayerAlreadySelected(player)) return;
    // Flag para determinar se seleccionó desde la cancha la acción de agregar jugador.
    const isSelectedFromPosition = state.selectedPlayerOnBuildTeam;
    if (!userInitialData?.idUsuarioDT) return;

    const response = await addNewPlayer({
      idJugadorTorneo: selectedPlayerId,
    });

    // Si no obtengo respuesta, early return.
    if (!response) return;

    // Handler para calcular la siguiente posición a elegir.
    const nextPositionResult = handleGetNextPosition(
      response,
      nombre,
      apellido
    );

    // Mensaje de success o error
    toast.show(nextPositionResult.mensaje);

    // Flag para determinar si todavia faltan posiciones por elegir en la cancha.
    const haveNextPositionAvailable = 'availablePosition' in nextPositionResult;

    if (haveNextPositionAvailable) {
      const nextPosition = teamDraft.find((team: PlayerF5) => {
        return team.orden === nextPositionResult.availablePosition?.orden;
      });
      const payload: TeamStateReducer = {
        playerDetailsModalOpen: false,
      };
      if (nextPosition && isSelectedFromPosition) {
        if (
          nextPositionResult.availablePosition?.position &&
          nextPositionResult.availablePosition?.orden !== undefined
        ) {
          nextPosition.posicion = {
            nombreCorto: nextPositionResult.availablePosition.position,
            id: nextPositionResult.availablePosition.orden,
          };
        }
        handleSelectPlayer(nextPosition);
        payload.selectedPosition =
          nextPositionResult.availablePosition?.position;
      }
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload,
      });
    } else {
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          selectedPlayerOnList: null,
          selectedPlayerOnBuildTeam: null,
          selectedPosition: null,
          playerDetailsModalOpen: false,
        },
      });
    }
  };

  // * Handler disparado por el "Detalle del jugador".
  const handlePlayerToField = async ({
    isCaptain,
    isInsuranced,
    player,
    willBeDeleted,
  }: DetailsPlayerSubmit) => {
    // eslint-disable-next-line no-constant-condition
    if (userInitialData?.idUsuarioDT && state.teamF5) {
      const teamDraft = cloneDeep(state.teamF5);
      if (teamDraft) {
        if (willBeDeleted) {
          // Jugador eliminado del equipo.
          deletePlayerFromTeam(player);
        } else if (isCaptain) {
          // Jugador designado como capitan del equipo.
          designateAsCaptain(player);
        } else if (isInsuranced) {
          // Jugador designado como asegurado del equipo.
          designateAsInsuranced(player);
        } else {
          // Jugador agregado al equipo.
          addPlayerToTeam(player, teamDraft as PlayerF5[]);
        }
      }
    } else {
      toast.show(TEXTS.defaultError);
    }
  };

  const handleSelectPlayer = (
    player: TemplatePlayer | PlayerF5 | PlayerMyTeamF5
  ) => {
    navigate('', { replace: false });
    if (isSelectedPlayerFromList(player)) {
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          selectedPlayerOnList: player,
          playerDetailsModalOpen: true,
        },
      });
    } else {
      if ('posicion' in player) {
        const hasPlayer = Boolean(player.id);
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            selectedPlayerOnBuildTeam: player,
            ...(!hasPlayer && {
              selectedPosition: player.posicion?.nombreCorto,
            }),
            ...(hasPlayer && { playerDetailsModalOpen: true }),
          },
        });
      }
    }
  };

  const handleResetHeaderInfo = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        showFiltersPlayersList: !state.showFiltersPlayersList,
      },
    });
  };

  // Esta funcion se encarga de dibujar los jugadores en la cancha
  const handleSetTeamOnField = (team: PlayerF5[]) => {
    const players = getPlayersOnField({
      team,
      handleSelectPlayer,
      context: EnumTeamContext.Build5,
    });

    const data = {
      type: TeamActionTypes.SET_STATE,
      payload: {
        teamF5: team,
        playersOnFieldF5: {
          goalkeeper: players.goalkeeper,
          fieldPlayers: players.fieldPlayers,
        },
      },
    };

    dispatch(data);

    return data.payload.playersOnFieldF5;
  };

  const infoFloatingCard = isMobile ? (
    <p className="text-sm text-left px-6 mt-3 mb-4">
      <strong>Armá tu equipo</strong>, elegí un arquero y cuatro jugadores de
      campo, sin restricción de puesto y con presupuesto ilimitado.
    </p>
  ) : (
    <div className="max-w-[760px] w-[95%] m-auto ">
      <Card className="relative z-[1] flex p-0 mt-[130px] h-[90px] items-center xl:px-8 w-full lg:px-4">
        <div className="text-left xl:scale-100 lg:scale-90">
          <p className="font-semibold text-sm text-gdt-gray-4">
            ARMÁ TU EQUIPO
          </p>
          <strong className="text-lg text-left lg:text-[#363546] font-extrabold">
            Fútbol 5
          </strong>
        </div>
        <p className="xl:text-sm text-left xl:px-6 flex-1 px-2 lg:text-xs">
          <strong>Armá tu equipo</strong>, elegí un arquero y cuatro
          <br />
          jugadores de campo, sin restricción de puesto y<br />
          con presupuesto ilimitado.
        </p>
        <div>
          <Button
            className="xl:w-44 lg:w-30"
            disabled={!state.teamIsComplete}
            type="button"
            variant="primary"
            onClick={() => {
              if (state.teamIsComplete) {
                handleSubmitNewTeam();
              }
            }}
          >
            Confirmar equipo
          </Button>
        </div>
      </Card>
    </div>
  );

  const closeInsurancedAndCaptainModal = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        insurancedAndCaptainPlayerModalOpen: false,
      },
    });
  };

  const insurancedAndCaptainModalContent = () => {
    const teamDraft = cloneDeep(state.teamF5);
    const message1 =
      'El Capitán duplicará la calificación de Clarín en cada fecha';
    const message2 =
      'el Jugador Asegurado sumará 5 puntos en caso de que no juegue.';

    let messageToShow = `${message1} y ${message2}`;
    if (teamDraft) {
      const hasOnlyCaptain = checkCaptainAndInsurancedProps(teamDraft, [
        'capitan',
      ]);
      const hasOnlyInsuranced = checkCaptainAndInsurancedProps(teamDraft, [
        'seguro',
      ]);
      if (hasOnlyCaptain) {
        messageToShow = uppercaseFirstLetter(message2) + message2.slice(1);
      } else if (hasOnlyInsuranced) {
        messageToShow = message1;
      }
    }

    return (
      <div>
        <span className="w-full inline-block pt-5 pb-5 text-center font-medium">
          {messageToShow}
        </span>
        <div className="text-center lg:pl-20 lg:pr-20">
          <Button
            className="w-full font-semibold no-underline items-center"
            type="button"
            variant="primary"
            onClick={closeInsurancedAndCaptainModal}
          >
            Elegir Jugadores
          </Button>
        </div>
      </div>
    );
  };

  const insurancedAndCaptainModalTitle = () => {
    const teamDraft = cloneDeep(state.teamF5);
    const message1 = 'un Capitán';
    const message2 = 'a tu Jugador Asegurado';
    const message3 = 'Seleccioná';

    let messageToShow = `${message3} ${message1} y ${message2}`;
    if (teamDraft) {
      const hasOnlyCaptain = checkCaptainAndInsurancedProps(teamDraft, [
        'capitan',
      ]);
      const hasOnlyInsuranced = checkCaptainAndInsurancedProps(teamDraft, [
        'seguro',
      ]);
      if (hasOnlyCaptain) {
        messageToShow = `${message3} ${message2}`;
      } else if (hasOnlyInsuranced) {
        messageToShow = `${message3} ${message1}`;
      }
    }

    return messageToShow;
  };

  const timeout =
    isLoadingAddPlayerToTeam ||
    isLoadingDesignateCaptainToPlayer ||
    isLoadingDesignateInsurancedToPlayer ||
    isLoadingRemovePlayerFromTeam ||
    isLoadingSaveTeam;

  const showLoader =
    isLoadingGetTeam ||
    componentIsLoading ||
    isFechingGetTeam ||
    isLoadingAddPlayerToTeam ||
    isLoadingDesignateCaptainToPlayer ||
    isLoadingDesignateInsurancedToPlayer ||
    isLoadingRemovePlayerFromTeam ||
    isLoadingSaveTeam;
  const isMounted = isDesktop && !componentIsLoading;

  const ConfirmMobileButton = () => (
    <Button
      className="w-[20.5rem] my-6"
      disabled={!state.teamIsComplete}
      type="button"
      variant="primary"
      onClick={() => {
        if (state.teamIsComplete) {
          handleSubmitNewTeam();
        }
      }}
    >
      Confirmar equipo
    </Button>
  );

  const onBackDropOnClick = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        selectedPlayerOnList: null,
        selectedPlayerOnBuildTeam: null,
        selectedPosition: null,
        showFiltersPlayersList: false,
        filtersApplied: {
          ...state.filtersApplied,
          positionF5: null,
        },
      },
    });
  };

  const onCloseController = () => {
    const isUsedPosition = Boolean(state.selectedPlayerOnBuildTeam?.id);

    if (showLoader) {
      return;
    }

    if (isDesktop) {
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          ...(isUsedPosition
            ? {
                selectedPlayerOnList: null,
                selectedPlayerOnBuildTeam: null,
                selectedPosition: null,
              }
            : {
                selectedPlayerOnBuildTeam: null,
              }),
          playerDetailsModalOpen: false,
        },
      });
    } else {
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          ...(isUsedPosition && {
            selectedPlayerOnList: null,
            selectedPlayerOnBuildTeam: null,
            selectedPosition: null,
          }),
          playerDetailsModalOpen: false,
        },
      });
    }
  };

  const onBackActionMobileSidebar = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        selectedPlayerOnList: null,
        selectedPlayerOnBuildTeam: null,
        selectedPosition: null,
        filtersApplied: {
          intoBudget: false,
          orderBy: null,
          orderClub: null,
          valuesRange: [MIN_INPUT_RANGE, MAX_INPUT_RANGE],
        },
      },
    });
  };

  const onCustomRightActionNavBar = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        showFiltersPlayersList: false,
        ...state.filtersApplied,
      },
    });
  };

  return (
    <>
      <Team5Layout title="Fútbol 5">
        <>
          {<Loader isVisible={showLoader} timeout={timeout} />}

          <FieldLayout.Main
            context={EnumTeamContext.Build5}
            onConfirmMobile={<ConfirmMobileButton />}
          >
            {toast.scopeToastContainer}
            {isMobile && infoFloatingCard}
            <FieldLayout.Inner>
              <FieldLayout.Field
                backDropOnClick={onBackDropOnClick}
                backDropOpen={
                  Boolean(state.selectedPlayerOnBuildTeam) ||
                  Boolean(state.showFiltersPlayersList)
                }
                context={EnumTeamContext.Build5}
                fieldPlayers={state.playersOnFieldF5?.fieldPlayers}
                floatingCard={infoFloatingCard}
                goalkeeper={state.playersOnFieldF5?.goalkeeper}
                isLoading={showLoader}
                isMounted={isMounted}
                selectedPositionOrden={
                  state.selectedPlayerOnBuildTeam &&
                  'orden' in state.selectedPlayerOnBuildTeam
                    ? state.selectedPlayerOnBuildTeam.orden
                    : undefined
                }
              />
            </FieldLayout.Inner>
          </FieldLayout.Main>
          {isMounted && (
            <FieldLayout.Sidebar>
              <DesktopSidebar
                context={EnumTeamContext.Build5}
                handleSelectPlayerOnList={handleSelectPlayer}
                onClick={handleResetHeaderInfo}
              />
            </FieldLayout.Sidebar>
          )}
        </>
      </Team5Layout>

      {/* Modal para detalle del jugador */}
      <Modal
        bottomSheetClassName="px-[16px]"
        className="px-[1.06rem] lg:px-11 py-10"
        controlledOnClose={onCloseController}
        isControlledOpen={Boolean(state.playerDetailsModalOpen)}
        render={() => {
          if (state.selectedPlayerOnList || state.selectedPlayerOnBuildTeam) {
            return (
              <DetailsPlayer
                context={EnumTeamContext.Build5}
                isLoading={isLoadingRemovePlayerFromTeam}
                playerId={
                  state.selectedPlayerOnBuildTeam?.id ??
                  state.selectedPlayerOnList?.id
                }
                playersOnFieldF5={state.playersOnFieldF5}
                selectedPlayerOnField={state.selectedPlayerOnBuildTeam}
                selectedPlayerOnList={state.selectedPlayerOnList}
                variant={state.selectedPlayerOnBuildTeam?.id ? 'delete' : 'add'}
                onSubmitPlayer={handlePlayerToField}
              />
            );
          }

          return null;
        }}
        size="xs"
        variant="controlled"
      />

      {/* Modal de advertencia sobre faltante de capitan y/o asegurado */}
      <Modal
        className="pt-14 pb-10 px-6 !max-w-[500px]"
        controlledOnClose={closeInsurancedAndCaptainModal}
        isControlledOpen={Boolean(state.insurancedAndCaptainPlayerModalOpen)}
        render={() => insurancedAndCaptainModalContent()}
        size="xs"
        title={insurancedAndCaptainModalTitle()}
        variant="controlled"
      />

      {/* SlideOver para seleccion de jugadores */}
      {
        <AnimatePresence>
          {Boolean(
            state.selectedPlayerOnBuildTeam &&
              !state.selectedPlayerOnBuildTeam?.id
          ) &&
            isMobile && (
              <motion.nav
                animate="show"
                className="fixed top-0 w-full z-50"
                initial="hide"
                variants={SLIDE_IN_OUT_LEFT}
              >
                <MobileSidebar
                  backAction={onBackActionMobileSidebar}
                  context={EnumTeamContext.Build5}
                  handleSelectPlayerOnList={handleSelectPlayer}
                  title={`Seleccioná un ${selectedPositionLabel}`}
                  onClick={handleResetHeaderInfo}
                />
              </motion.nav>
            )}
        </AnimatePresence>
      }

      {/* SlideOver para filtros */}
      <AnimatePresence>
        {state.showFiltersPlayersList && isMobile && (
          <motion.nav
            animate="show"
            className="fixed top-0 w-full z-50"
            initial="hide"
            variants={SLIDE_IN_OUT_LEFT}
          >
            <NavBar
              mobile={{
                rightContent: {
                  customRightAction: () => {
                    onCustomRightActionNavBar();
                  },
                },
                centerContent: {
                  title: `Filtros`,
                },
              }}
            >
              <FormFilterPlayers context={EnumTeamContext.Build5} />
            </NavBar>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};
